import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Trophy,
  Tags,
  Users,
  ClipboardList,
  BarChart3,
  UserCog,
  LogOut,
  Award,
  Calendar,
  Star,
  Monitor,
  DollarSign,
  UserPlus,
  Ticket,
  Database,
  Users2,
  Radio,
  Tv,
  GraduationCap,
  BriefcaseBusiness,
  Printer,
  ListChecks,
  Settings,
  Menu,
  X,
  Medal,
} from "lucide-react";

const ROLE_LABEL = { admin: "Admin", judge: "Árbitro", athlete: "Atleta", operator: "Operadora" };

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isAthlete = user?.role === "athlete";
  const isOperator = user?.role === "operator";
  const [pendingAccess, setPendingAccess] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    const load = () =>
      api
        .get("/admin/access-requests/count")
        .then((r) => setPendingAccess(r.data.pending || 0))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAdmin]);

  const links = useMemo(() => {
    if (isAthlete) {
      return [
        { to: "/minhas-inscricoes", label: "Minhas Inscrições", icon: Ticket },
        { to: "/treinadores", label: "Meu Ranking (Treinador)", icon: GraduationCap },
      ];
    }
    if (isOperator) {
      return [
        { to: "/meus-campeonatos", label: "Meus Campeonatos", icon: Trophy },
        { to: "/inscricoes", label: "Pagamentos", icon: UserPlus },
        { to: "/atletas-categoria", label: "Atletas", icon: ListChecks },
        { to: "/impressoes", label: "Impressões", icon: Printer },
        { to: "/premiacao", label: "Premiação", icon: Medal },
      ];
    }
    const all = [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, adminOnly: false },
      { to: "/competicoes", label: "Competições", icon: Trophy, adminOnly: true },
      { to: "/categorias", label: "Categorias", icon: Tags, adminOnly: true },
      { to: "/atletas", label: "Atletas", icon: Users, adminOnly: true },
      { to: "/lotes", label: "Lotes", icon: DollarSign, adminOnly: true },
      { to: "/inscricoes", label: "Pagamentos", icon: UserPlus, adminOnly: true },
      { to: "/roteiro", label: "Roteiro", icon: Calendar, adminOnly: true },
      { to: "/premiacao", label: "Premiação", icon: Medal, adminOnly: true },
      { to: "/maestro", label: "Maestro", icon: Radio, adminOnly: true },
      { to: "/backstage", label: "Monitor Backstage", icon: Tv, adminOnly: true, external: true },
      { to: "/sumulas", label: "Súmulas", icon: ClipboardList, adminOnly: false },
      { to: "/ranking", label: "Classificação", icon: Award, adminOnly: false },
      { to: "/overalls", label: "Overall", icon: Star, adminOnly: true },
      { to: "/equipes", label: "Equipes", icon: Users2, adminOnly: true },
      { to: "/treinadores", label: "Treinadores", icon: GraduationCap, adminOnly: false },
      { to: "/telao", label: "Telão", icon: Monitor, adminOnly: false, external: true },
      { to: "/usuarios", label: "Usuários", icon: UserCog, adminOnly: true },
      { to: "/operadoras", label: "Operadoras", icon: BriefcaseBusiness, adminOnly: true, badge: pendingAccess },
      { to: "/backup", label: "Backup", icon: Database, adminOnly: true },
      { to: "/configuracoes", label: "Configurações", icon: Settings, adminOnly: true },
    ];
    return all.filter((l) => (l.adminOnly ? isAdmin : true));
  }, [isAdmin, isAthlete, isOperator, pendingAccess]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="px-6 mb-4 md:mb-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="/logo.jpg"
              alt="FITS4YOU"
              className="w-11 h-11 rounded-sm object-contain bg-white p-1 shrink-0"
              data-testid="brand-fits4you"
            />
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-none tracking-tight truncate">
                FITS4YOU
              </div>
              <div className="text-[8px] uppercase tracking-[0.15em] text-[#C9A84C] mt-0.5 font-bold leading-tight">
                Championship Suite
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <img
              src="/logo-npl.png"
              alt="NPL - National Physique League"
              className="w-11 h-11 rounded-sm object-contain shrink-0"
              data-testid="brand-npl"
              title="NPL - National Physique League"
            />
            <button
              type="button"
              className="md:hidden btn-ghost !p-2"
              data-testid="sidebar-menu-toggle"
              aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <nav className={`${menuOpen ? "flex" : "hidden"} md:flex flex-col`} data-testid="sidebar-nav">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="link-nav"
              >
                <l.icon size={17} strokeWidth={2} />
                <span>{l.label}</span>
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  "link-nav" + (isActive ? " active" : "")
                }
              >
                <l.icon size={17} strokeWidth={2} />
                <span>{l.label}</span>
                {l.badge > 0 && (
                  <span
                    data-testid="nav-operadoras-badge"
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#C9A84C] text-black text-[11px] font-black flex items-center justify-center"
                  >
                    {l.badge}
                  </span>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className={`${menuOpen ? "block" : "hidden"} md:block mt-8 px-6`} data-testid="sidebar-session">
          <div className="overline mb-2">Sessão</div>
          <div className="text-sm text-white truncate">{user?.name}</div>
          <div className="text-xs text-[var(--text-secondary)] truncate mb-3">
            {user?.email}
          </div>
          <span
            className={`chip ${isAdmin ? "chip-admin" : isOperator ? "chip-success" : "chip-judge"}`}
            data-testid="session-role-chip"
          >
            {ROLE_LABEL[user?.role] || "Árbitro"}
          </span>
          <button
            data-testid="logout-button"
            className="btn-ghost w-full mt-4 flex items-center gap-2 justify-center"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
          >
            <LogOut size={15} /> Sair
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
